// Medical-aid coverage on the record's Medical aid tab (design/screens/patient-record.html "Medical-aid coverage";
// FIXTURE §10.7): the per-product overrides and the overall caps a scheme's letter grants (UC-PATIENTS-017, PAT-81..83),
// read and replaced through /patients/{id}/coverage-overrides/{products,overall}. Stored only this sprint — the pricing
// engine of Sprint 5 applies them — and the screen says so. Money is typed in rand and sent as integer cents, percentages
// as integer basis points, both built from the typed digits, never through a float. No Control PIN: the inventory has none
// on these routes (D61; the prototype's lock mark awaits the owner). A dependant reading the main account's membership sees
// the main account's rows read-only (A1).
import {
  BENEFIT_YEAR_BASES, COVERAGE_OVERRIDE_TYPES, OverallCoverageOverrideInputSchema, ProductCoverageOverrideInputSchema,
  type CoverageOverrideType, type OverallCoverageOverrideInput, type OverallCoverageOverrideViewSchema, type PatientView, type ProductCoverageOverrideInput,
  type ProductCoverageOverrideViewSchema,
} from "@visionx/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router";
import type { z } from "zod";
import { coverage } from "../api/endpoints.patients";
import { describeError, isApiError } from "../api/problem";
import { formatDate, formatMoney } from "../lib/format";
import { can, useSessionView } from "../session/store";
import { useWriteBlock } from "../shell/OfflineBanner";
import { Alert, ErrorAlert, Loading, Select, TextInput } from "../ui/bits";
import { Icon } from "../ui/icons";
import { recordPath, writeTitle } from "./PatientsShared";

export const COVERAGE_TYPE_LABEL: Record<CoverageOverrideType, string> = { Consultation: "Consultation", Lens: "Lenses", Frame: "Frame", ContactLens: "Contact lenses" };
const BASIS_LABEL: Record<(typeof BENEFIT_YEAR_BASES)[number], string> = { CalendarYear: "Calendar year", SchemeAnniversary: "Scheme anniversary", MemberJoinDate: "Member's join date" };
const STORED_ONLY = "Stored only — nothing prices a line from these until the pricing engine (Sprint 5).";

/** `1 800.00`, `R1800`, `1800,5` → 180000 cents, from the digits; blank → null; anything else → NaN. */
export function randToCents(text: string): number | null {
  const t = text.replace(/^\s*R\s*/i, "").replace(/\s/g, ""); // \s also covers the no-break spaces Intl groups digits with
  if (t === "") return null;
  const m = /^(\d{1,9})(?:[.,](\d{1,2}))?$/.exec(t);
  return m ? Number(`${m[1]}${(m[2] ?? "").padEnd(2, "0")}`) : Number.NaN;
}
/** `12.5` or `12,5%` → 1250 basis points, from the digits; blank → null; anything else → NaN. */
export function percentToBp(text: string): number | null {
  const t = text.replace(/%\s*$/, "").trim();
  if (t === "") return null;
  const m = /^(\d{1,3})(?:[.,](\d{1,2}))?$/.exec(t);
  return m ? Number(`${m[1]}${(m[2] ?? "").padEnd(2, "0")}`) : Number.NaN;
}
/** Basis points as the design writes a percentage: `12.5%`, `100%`. */
export function formatBp(bp: number): string {
  const frac = String(bp % 100).padStart(2, "0").replace(/0$/, "");
  return `${Math.trunc(bp / 100)}${bp % 100 === 0 ? "" : `.${frac}`}%`;
}
const centsText = (c: number | null): string => (c === null ? "" : formatMoney(c).replace(/^R /, ""));
const bpText = (bp: number | null): string => (bp === null ? "" : formatBp(bp).replace(/%$/, ""));

function windowText(from: string | null, to: string | null): string {
  if (from && to) return `${formatDate(from)} – ${formatDate(to)}`;
  if (from) return `from ${formatDate(from)}`;
  if (to) return `until ${formatDate(to)}`;
  return "no end date";
}

type ProductView = z.output<typeof ProductCoverageOverrideViewSchema>;
type OverallView = z.output<typeof OverallCoverageOverrideViewSchema>;

export function productCaps(o: Pick<ProductView, "rateMaxCents" | "ratePercentBp" | "discountBp">): string {
  return [
    o.rateMaxCents !== null ? `max ${formatMoney(o.rateMaxCents)}` : null,
    o.ratePercentBp !== null ? `${formatBp(o.ratePercentBp)} of the rate` : null,
    o.discountBp !== null ? `${formatBp(o.discountBp)} discount` : null,
  ].filter(Boolean).join(" · ");
}

/** Both lists for one patient; every role with `patients.medical_aid.write` reads them (BR-PATIENTS-021). */
function useCoverage(p: PatientView) {
  const sess = useSessionView();
  const allowed = can(sess, "patients.medical_aid.write");
  const products = useQuery({ queryKey: ["patient", p.id, "coverage", "products"], queryFn: () => coverage.products(p.id), enabled: allowed });
  const overall = useQuery({ queryKey: ["patient", p.id, "coverage", "overall"], queryFn: () => coverage.overall(p.id), enabled: allowed });
  const inheritedFrom = products.data?.data.inheritedFrom ?? overall.data?.data.inheritedFrom ?? null;
  return { allowed, products, overall, inheritedFrom };
}

/** The prototype's "Medical-aid coverage" panel: what is stored, and the way into the editor. */
export function CoverageSummary({ p, editing, onEdit }: { p: PatientView; editing: boolean; onEdit: () => void }) {
  const { allowed, products, overall, inheritedFrom } = useCoverage(p);
  const block = useWriteBlock();
  const items = products.data?.data.items ?? [];
  const caps = overall.data?.data.items ?? [];
  const none = items.length === 0 && caps.length === 0;
  const canEdit = allowed && !inheritedFrom && !editing;
  return (
    <div className="panel">
      <div className="panel-head"><h2>Medical-aid coverage</h2><span className="meta">overrides from the scheme's letter</span></div>
      <div className="panel-body">
        {!allowed ? <span className="help">Coverage overrides are read with the medical-aid permission (patients.medical_aid.write).</span> : (
          <div className="stack gap-8">
            {(products.isPending || overall.isPending) && <Loading what="coverage overrides" />}
            <ErrorAlert error={products.error ?? overall.error} />
            <dl className="kv">
              <dt>Tariff in force</dt><dd><span className="faint">Tariff tables arrive with the pricing engine (Sprint 5)</span></dd>
              {inheritedFrom && <><dt>Source</dt><dd>The main member's overrides, read at use time — <Link className="mono" to={recordPath(inheritedFrom.patientId, "medical-aid")}>{inheritedFrom.accountCode}</Link></dd></>}
              {products.data && overall.data && none && <><dt>Overrides</dt><dd>None</dd></>}
            </dl>
          </div>
        )}
      </div>
      {items.length > 0 && (
        <div className="list" aria-label="Product overrides">
          {items.map((o) => (
            <div key={o.id} className="item">
              <div className="grow"><div className="primary">{COVERAGE_TYPE_LABEL[o.type]}: {productCaps(o)}</div><div className="sub">{windowText(o.effectiveFrom, o.effectiveTo)}{o.notes ? ` · ${o.notes}` : ""} · added {formatDate(o.createdAt)}</div></div>
            </div>
          ))}
        </div>
      )}
      {caps.length > 0 && (
        <div className="list" aria-label="Overall caps">
          {caps.map((o) => (
            <div key={o.id} className="item">
              <div className="grow">
                <div className="primary">{o.overallMaxCents !== null && <>Overall max <span className="nw">{formatMoney(o.overallMaxCents)}</span></>}{o.overallMaxCents !== null && o.perMemberCents !== null && " · "}{o.perMemberCents !== null && <>per member <span className="nw">{formatMoney(o.perMemberCents)}</span></>}</div>
                <div className="sub">{o.membershipNumber ? <>member <span className="mono">{o.membershipNumber}</span> · </> : null}{BASIS_LABEL[o.benefitYearBasis].toLowerCase()}{o.benefitPeriodStart || o.benefitPeriodEnd ? ` · ${windowText(o.benefitPeriodStart, o.benefitPeriodEnd)}` : ""} · used <span className="nw">{formatMoney(o.consumedToDateCents)}</span> (from invoices, Sprint 6){o.notes ? ` · ${o.notes}` : ""}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="panel-foot">
        {allowed && !inheritedFrom && <button className="btn sm" type="button" disabled={!canEdit || block !== null || !products.data || !overall.data} title={writeTitle(block)} onClick={onEdit}>{none ? <><Icon name="plus" />Add override</> : "Edit overrides"}</button>}
        <span className="right small faint">{STORED_ONLY}</span>
      </div>
    </div>
  );
}

// --- the editor --------------------------------------------------------------------------------------------------------

interface ProductDraft { key: number; type: CoverageOverrideType; rateMax: string; ratePercent: string; discount: string; from: string; to: string; notes: string }
interface OverallDraft { key: number; membershipNumber: string; overallMax: string; perMember: string; basis: (typeof BENEFIT_YEAR_BASES)[number]; start: string; end: string; notes: string }
type Errors = Record<string, string>;
/** The contract's field name → the draft's, so a schema issue or a server `0.rateMaxCents` lands on its input. */
const PRODUCT_FIELD: Record<string, keyof ProductDraft> = { type: "type", rateMaxCents: "rateMax", ratePercentBp: "ratePercent", discountBp: "discount", effectiveFrom: "from", effectiveTo: "to", notes: "notes" };
const OVERALL_FIELD: Record<string, keyof OverallDraft> = { membershipNumber: "membershipNumber", overallMaxCents: "overallMax", perMemberCents: "perMember", benefitYearBasis: "basis", benefitPeriodStart: "start", benefitPeriodEnd: "end", notes: "notes" };

let nextKey = 1;
const productDraft = (o?: ProductView): ProductDraft => ({
  key: nextKey++, type: o?.type ?? "Frame", rateMax: centsText(o?.rateMaxCents ?? null), ratePercent: bpText(o?.ratePercentBp ?? null), discount: bpText(o?.discountBp ?? null),
  from: o?.effectiveFrom ?? "", to: o?.effectiveTo ?? "", notes: o?.notes ?? "",
});
const overallDraft = (o?: OverallView, membershipNumber?: string | null): OverallDraft => ({
  key: nextKey++, membershipNumber: o ? (o.membershipNumber ?? "") : (membershipNumber ?? ""), overallMax: centsText(o?.overallMaxCents ?? null), perMember: centsText(o?.perMemberCents ?? null),
  basis: o?.benefitYearBasis ?? "CalendarYear", start: o?.benefitPeriodStart ?? "", end: o?.benefitPeriodEnd ?? "", notes: o?.notes ?? "",
});

const MONEY_HINT = "an amount in rand, e.g. 1 800.00";
const PERCENT_HINT = "a percentage from 0 to 100, e.g. 12.5";
/** One draft row → the wire row, with every refusal named by field before any request: the typed number first, then the contract's rules. */
function productInput(d: ProductDraft, row: number, errors: Errors): ProductCoverageOverrideInput | null {
  const bad = (f: keyof ProductDraft, m: string) => { errors[`${row}.${f}`] = m; };
  const rateMaxCents = randToCents(d.rateMax), ratePercentBp = percentToBp(d.ratePercent), discountBp = percentToBp(d.discount);
  if (Number.isNaN(rateMaxCents)) bad("rateMax", `Enter ${MONEY_HINT}`);
  if (Number.isNaN(ratePercentBp)) bad("ratePercent", `Enter ${PERCENT_HINT}`);
  if (Number.isNaN(discountBp)) bad("discount", `Enter ${PERCENT_HINT}`);
  if ([rateMaxCents, ratePercentBp, discountBp].some((v) => Number.isNaN(v))) return null;
  const raw: z.input<typeof ProductCoverageOverrideInputSchema> = {
    type: d.type,
    ...(rateMaxCents !== null ? { rateMaxCents } : {}), ...(ratePercentBp !== null ? { ratePercentBp } : {}), ...(discountBp !== null ? { discountBp } : {}),
    ...(d.from ? { effectiveFrom: d.from } : {}), ...(d.to ? { effectiveTo: d.to } : {}), ...(d.notes.trim() ? { notes: d.notes.trim() } : {}),
  };
  const parsed = ProductCoverageOverrideInputSchema.safeParse(raw);
  if (parsed.success) return parsed.data;
  for (const i of parsed.error.issues) bad(PRODUCT_FIELD[String(i.path[0])] ?? "rateMax", i.message === "at least one cap is required" ? "Enter a rate max, a rate % or a discount %" : i.message);
  return null;
}
function overallInput(d: OverallDraft, row: number, errors: Errors): OverallCoverageOverrideInput | null {
  const bad = (f: keyof OverallDraft, m: string) => { errors[`${row}.${f}`] = m; };
  const overallMaxCents = randToCents(d.overallMax), perMemberCents = randToCents(d.perMember);
  if (Number.isNaN(overallMaxCents)) bad("overallMax", `Enter ${MONEY_HINT}`);
  if (Number.isNaN(perMemberCents)) bad("perMember", `Enter ${MONEY_HINT}`);
  if (Number.isNaN(overallMaxCents) || Number.isNaN(perMemberCents)) return null;
  const raw: z.input<typeof OverallCoverageOverrideInputSchema> = {
    benefitYearBasis: d.basis,
    ...(d.membershipNumber.trim() ? { membershipNumber: d.membershipNumber.trim() } : {}),
    ...(overallMaxCents !== null ? { overallMaxCents } : {}), ...(perMemberCents !== null ? { perMemberCents } : {}),
    ...(d.start ? { benefitPeriodStart: d.start } : {}), ...(d.end ? { benefitPeriodEnd: d.end } : {}), ...(d.notes.trim() ? { notes: d.notes.trim() } : {}),
  };
  const parsed = OverallCoverageOverrideInputSchema.safeParse(raw);
  if (parsed.success) return parsed.data;
  for (const i of parsed.error.issues) bad(OVERALL_FIELD[String(i.path[0])] ?? "overallMax", i.message === "an overall or a per-member cap is required" ? "Enter an overall max or a per-member cap" : i.message);
  return null;
}
/** A server refusal `<row>.<field>` onto the draft's input; anything without a row goes to the editor's alert. */
function serverErrors(err: unknown, fields: Record<string, string>): Errors | null {
  if (!isApiError(err) || err.code !== "VALIDATION_FAILED") return null;
  const out: Errors = {};
  for (const e of err.errors) {
    const m = /^(\d+)\.(\w+)/.exec(e.path);
    if (m && fields[m[2]!]) out[`${m[1]}.${fields[m[2]!]}`] = e.message;
  }
  return Object.keys(out).length > 0 ? out : null;
}

/** The editor below the panel: both lists edited whole, each saved with one PUT when it changed. */
export function CoverageEditor({ p, onClose, onSaved }: { p: PatientView; onClose: () => void; onSaved: (n: ReactNode) => void }) {
  const qc = useQueryClient();
  const { products, overall } = useCoverage(p);
  const block = useWriteBlock();
  const [rows, setRows] = useState<ProductDraft[]>(() => {
    const items = products.data?.data.items ?? [];
    return items.length > 0 ? items.map((o) => productDraft(o)) : (overall.data?.data.items.length ?? 0) > 0 ? [] : [productDraft()];
  });
  const [caps, setCaps] = useState<OverallDraft[]>(() => (overall.data?.data.items ?? []).map((o) => overallDraft(o)));
  const [pErr, setPErr] = useState<Errors>({});
  const [oErr, setOErr] = useState<Errors>({});
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const first = useRef<HTMLDivElement>(null);
  useEffect(() => { first.current?.querySelector<HTMLElement>("select, input")?.focus(); }, []);
  // What is stored, as the wire rows, so an unchanged list is not put again; a list put by a Save that then failed moves it.
  const initial = useRef({
    products: JSON.stringify((products.data?.data.items ?? []).map((o) => productInput(productDraft(o), 0, {}))),
    overall: JSON.stringify((overall.data?.data.items ?? []).map((o) => overallInput(overallDraft(o), 0, {}))),
  });
  const setRow = (i: number, patch: Partial<ProductDraft>) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const setCap = (i: number, patch: Partial<OverallDraft>) => setCaps((cs) => cs.map((c, j) => (j === i ? { ...c, ...patch } : c)));

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const pe: Errors = {}, oe: Errors = {};
    const pIn = rows.map((r, i) => productInput(r, i, pe));
    const oIn = caps.map((c, i) => overallInput(c, i, oe));
    setPErr(pe);
    setOErr(oe);
    setError(null);
    if (Object.keys(pe).length > 0 || Object.keys(oe).length > 0) return;
    const productBody = pIn as ProductCoverageOverrideInput[];
    const overallBody = oIn as OverallCoverageOverrideInput[];
    const changed = { products: JSON.stringify(productBody) !== initial.current.products, overall: JSON.stringify(overallBody) !== initial.current.overall };
    if (!changed.products && !changed.overall) { onClose(); return; }
    setBusy(true);
    let stage: "products" | "overall" = "products";
    try {
      if (changed.products) await coverage.putProducts(p.id, productBody);
      stage = "overall";
      if (changed.overall) await coverage.putOverall(p.id, overallBody);
      void qc.invalidateQueries({ queryKey: ["patient", p.id, "coverage"] });
      onSaved(<Alert tone="ok"><b>Coverage overrides saved.</b> Replaced rows are kept as history, never deleted. {STORED_ONLY}</Alert>);
    } catch (err) {
      // A list already put stays put: read again so a second Save sends only what is still to be saved.
      void qc.invalidateQueries({ queryKey: ["patient", p.id, "coverage"] });
      const mapped = serverErrors(err, stage === "products" ? PRODUCT_FIELD : OVERALL_FIELD);
      if (mapped) (stage === "products" ? setPErr : setOErr)(mapped);
      else setError(err);
      if (stage === "overall" && changed.products) initial.current.products = JSON.stringify(productBody);
    } finally { setBusy(false); }
  };

  const cell = (id: string, label: string, value: string, set: (v: string) => void, err: string | undefined, o: { mono?: boolean; type?: string; width?: number; inputMode?: "decimal" } = {}) => (
    <td>
      <TextInput id={id} aria-label={label} aria-invalid={err ? true : undefined} value={value} onChange={(e) => set(e.target.value)} error={!!err} mono={o.mono} type={o.type ?? "text"} inputMode={o.inputMode}
        style={{ ...(o.width ? { width: o.width } : {}), ...(o.inputMode === "decimal" ? { textAlign: "right" as const } : {}) }} />
      {err && <div className="error" role="alert" style={o.width ? { maxWidth: o.width + 24 } : undefined}>{err}</div>}
    </td>
  );

  return (
    <form className="panel" aria-label="Edit coverage overrides" onSubmit={(e) => void save(e)}>
      <div className="panel-head"><h2>Edit coverage overrides</h2><span className="meta">{p.firstNames} {p.surname} · <span className="mono">{p.accountCode}</span></span></div>
      <div className="panel-body stack gap-8" ref={first}>
        <Alert tone="info">{STORED_ONLY} Amounts in rand (<span className="mono">1 800.00</span>), percentages to two decimals; a row needs at least one cap, and windows of one product may not overlap.</Alert>
        {error !== null && <Alert tone="crit"><b>Not saved.</b> {describeError(error)}</Alert>}
        <div className="small strong muted">Product overrides</div>
        <div className="tbl-scroll">
          <table>
            <thead><tr><th>Product</th><th className="r">Rate max (R)</th><th className="r">Rate %</th><th className="r">Discount %</th><th>From</th><th>To</th><th>Notes</th><th className="r"></th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.key}>
                  <td><Select aria-label={`Product, row ${i + 1}`} style={{ width: 118 }} value={r.type} onChange={(e) => setRow(i, { type: e.target.value as CoverageOverrideType })}>{COVERAGE_OVERRIDE_TYPES.map((t) => <option key={t} value={t}>{COVERAGE_TYPE_LABEL[t]}</option>)}</Select>{pErr[`${i}.type`] && <div className="error" role="alert">{pErr[`${i}.type`]}</div>}</td>
                  {cell(`cov-max-${r.key}`, `Rate max (R), row ${i + 1}`, r.rateMax, (v) => setRow(i, { rateMax: v }), pErr[`${i}.rateMax`], { mono: true, inputMode: "decimal", width: 88 })}
                  {cell(`cov-pct-${r.key}`, `Rate %, row ${i + 1}`, r.ratePercent, (v) => setRow(i, { ratePercent: v }), pErr[`${i}.ratePercent`], { mono: true, inputMode: "decimal", width: 52 })}
                  {cell(`cov-disc-${r.key}`, `Discount %, row ${i + 1}`, r.discount, (v) => setRow(i, { discount: v }), pErr[`${i}.discount`], { mono: true, inputMode: "decimal", width: 52 })}
                  {cell(`cov-from-${r.key}`, `From, row ${i + 1}`, r.from, (v) => setRow(i, { from: v }), pErr[`${i}.from`], { type: "date" })}
                  {cell(`cov-to-${r.key}`, `To, row ${i + 1}`, r.to, (v) => setRow(i, { to: v }), pErr[`${i}.to`], { type: "date" })}
                  {cell(`cov-notes-${r.key}`, `Notes, row ${i + 1}`, r.notes, (v) => setRow(i, { notes: v }), pErr[`${i}.notes`], { width: 120 })}
                  <td className="r"><button className="btn sm" type="button" aria-label={`Remove product override, row ${i + 1}`} onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && <span className="help">No product overrides — the tariff step applies to every product.</span>}
        <div><button className="btn sm" type="button" onClick={() => setRows((rs) => [...rs, productDraft()])}><Icon name="plus" />Add product override</button></div>
        <div className="small strong muted">Overall and per-member caps</div>
        <div className="tbl-scroll">
          <table>
            <thead><tr><th>Member number</th><th className="r">Overall max (R)</th><th className="r">Per member (R)</th><th>Benefit year</th><th>Period start</th><th>Period end</th><th>Notes</th><th className="r"></th></tr></thead>
            <tbody>
              {caps.map((c, i) => (
                <tr key={c.key}>
                  {cell(`cap-no-${c.key}`, `Member number, row ${i + 1}`, c.membershipNumber, (v) => setCap(i, { membershipNumber: v }), oErr[`${i}.membershipNumber`], { mono: true, width: 88 })}
                  {cell(`cap-max-${c.key}`, `Overall max (R), row ${i + 1}`, c.overallMax, (v) => setCap(i, { overallMax: v }), oErr[`${i}.overallMax`], { mono: true, inputMode: "decimal", width: 80 })}
                  {cell(`cap-per-${c.key}`, `Per member (R), row ${i + 1}`, c.perMember, (v) => setCap(i, { perMember: v }), oErr[`${i}.perMember`], { mono: true, inputMode: "decimal", width: 80 })}
                  <td><Select aria-label={`Benefit year, row ${i + 1}`} style={{ width: 124 }} value={c.basis} onChange={(e) => setCap(i, { basis: e.target.value as OverallDraft["basis"] })}>{BENEFIT_YEAR_BASES.map((b) => <option key={b} value={b}>{BASIS_LABEL[b]}</option>)}</Select></td>
                  {cell(`cap-start-${c.key}`, `Period start, row ${i + 1}`, c.start, (v) => setCap(i, { start: v }), oErr[`${i}.start`], { type: "date" })}
                  {cell(`cap-end-${c.key}`, `Period end, row ${i + 1}`, c.end, (v) => setCap(i, { end: v }), oErr[`${i}.end`], { type: "date" })}
                  {cell(`cap-notes-${c.key}`, `Notes, cap row ${i + 1}`, c.notes, (v) => setCap(i, { notes: v }), oErr[`${i}.notes`], { width: 96 })}
                  <td className="r"><button className="btn sm" type="button" aria-label={`Remove cap, row ${i + 1}`} onClick={() => setCaps((cs) => cs.filter((_, j) => j !== i))}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {caps.length === 0 && <span className="help">No overall cap — the scheme's own annual benefit applies.</span>}
        <div><button className="btn sm" type="button" onClick={() => setCaps((cs) => [...cs, overallDraft(undefined, p.medicalAid?.membershipNumber)])}><Icon name="plus" />Add overall cap</button></div>
      </div>
      <div className="panel-foot">
        <button className="btn" type="button" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn primary" type="submit" disabled={busy || block !== null} title={writeTitle(block)}>{busy ? "Saving…" : "Save overrides"}</button>
        <span className="right small faint">Replaced rows are voided and kept as history, never deleted</span>
      </div>
    </form>
  );
}
